/**
 * @fileoverview Theme management hook and context provider
 * Manages application-wide theme state and persistence
 * Supports multiple theme variants including dark, light, autumn, winter, and minimal
 * Includes fallback mechanism for theme.json parsing failures
 */

import { createContext, useContext, useEffect, useState } from "react";

/**
 * Available theme variants for the application
 * Each theme has its own color scheme and styling defined in theme.json
 * 
 * IMPORTANT: These values must match exactly what's expected in theme.json
 * Valid values for theme.json schema:
 * - variant: "professional" | "tint" | "vibrant"
 * - appearance: "light" | "dark" | "system"
 */
type Theme = "dark" | "light" | "autumn" | "winter" | "minimal";

// Map our application themes to the schema expected by the theme.json plugin
const themeToVariantMap: Record<Theme, "professional" | "tint" | "vibrant"> = {
  dark: "professional",
  light: "professional",
  autumn: "tint",
  winter: "tint",
  minimal: "vibrant"
};

/**
 * Fallback CSS variables for when theme.json parsing fails
 * These provide a basic color scheme to ensure the app remains usable
 */
const fallbackThemeVars: Record<Theme, Record<string, string>> = {
  dark: {
    "--background": "#000000",
    "--foreground": "#ffffff",
    "--primary": "#1a8652",
    "--primary-foreground": "#ffffff"
  },
  light: {
    "--background": "#ffffff",
    "--foreground": "#000000",
    "--primary": "#1a8652",
    "--primary-foreground": "#ffffff"
  },
  autumn: {
    "--background": "#f5f5f4",
    "--foreground": "#1c1917",
    "--primary": "#d97706",
    "--primary-foreground": "#ffffff"
  },
  winter: {
    "--background": "#f8fafc",
    "--foreground": "#0f172a",
    "--primary": "#0ea5e9",
    "--primary-foreground": "#ffffff"
  },
  minimal: {
    "--background": "#fafafa",
    "--foreground": "#171717",
    "--primary": "#18181b",
    "--primary-foreground": "#ffffff"
  }
};

/**
 * Props for the ThemeProvider component
 * @property children - React child components
 * @property defaultTheme - Optional default theme to use if none is stored
 */
type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
};

/**
 * Theme context state interface
 * @property theme - Current active theme
 * @property setTheme - Function to update the current theme
 * @property themeError - Whether a theme parsing error occurred
 */
type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  themeError: boolean;
};

/**
 * Context for providing theme state throughout the application
 * Defaults to dark theme if no provider is present
 */
const ThemeProviderContext = createContext<ThemeProviderState>({
  theme: "dark",
  setTheme: () => null,
  themeError: false
});

/**
 * Applies fallback CSS variables directly to ensure theme works
 * even when theme.json parsing fails
 */
function applyFallbackThemeVars(theme: Theme) {
  const root = window.document.documentElement;
  const vars = fallbackThemeVars[theme];
  
  // Apply all CSS variables for the current theme
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Mark document as using fallback theme
  root.classList.add('using-fallback-theme');
}

/**
 * Theme provider component
 * Manages theme state and persistence in localStorage
 * Automatically applies theme classes to document root
 * Includes fallback mechanism for theme.json parsing failures
 * 
 * @param props - Component props including children and optional default theme
 * @returns ThemeProvider component
 */
export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem("theme") as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });
  
  // Track if we're using fallback theme due to theme.json parsing error
  const [themeError, setThemeError] = useState(false);

  useEffect(() => {
    // Detect theme.json parsing errors by checking if CSS variables were properly applied
    const detectThemeError = () => {
      const root = window.document.documentElement;
      const computedStyle = getComputedStyle(root);
      const primaryColor = computedStyle.getPropertyValue('--primary').trim();
      
      // If primary color is not set or empty, assume theme.json parsing failed
      if (!primaryColor) {
        console.warn('[Theme] Detected missing theme CSS variables, applying fallback theme');
        setThemeError(true);
        applyFallbackThemeVars(theme);
      } else {
        setThemeError(false);
        root.classList.remove('using-fallback-theme');
      }
    };
    
    const root = window.document.documentElement;
    // Remove all theme classes without transition
    root.classList.remove("light", "dark", "autumn", "winter", "minimal");
    // Add new theme class immediately
    root.classList.add(theme);
    
    // Schedule theme error detection after a short delay to allow CSS to apply
    const timeoutId = setTimeout(detectThemeError, 300);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      try {
        localStorage.setItem("theme", newTheme);
        setTheme(newTheme);
        if (themeError) {
          // If we're using fallback theme, apply new theme vars immediately
          applyFallbackThemeVars(newTheme);
        }
      } catch (e) {
        setTheme(newTheme);
        if (themeError) {
          applyFallbackThemeVars(newTheme);
        }
      }
    },
    themeError
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Hook for accessing the current theme and theme updating function
 * Must be used within a ThemeProvider component
 * 
 * @throws Error if used outside of ThemeProvider
 * @returns Object containing current theme and setTheme function
 */
export function useTheme() {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}